import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function LoginPage() {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // فرض الوضع النهاري
  useEffect(() => {
    document.documentElement.removeAttribute('data-theme');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, formData, {
        withCredentials: true
      });

      if (response.data.user) {
        // حفظ معلومات المستخدم في localStorage
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('userId', response.data.user.user_id);
        navigate('/dashboard');
      }
    } catch (err) {
      // رسائل خطأ واضحة بحسب الحالة
      const status = err.response?.status;
      let errorMsg;
      if (status === 401) {
        errorMsg = '❌ البريد الإلكتروني أو كلمة المرور غير صحيحة. تأكد من الرموز الخاصة (#، @، إلخ) في كلمة السر';
      } else if (status === 429) {
        errorMsg = '⏱️ محاولات كثيرة - انتظر دقيقة ثم حاول مجدداً';
      } else if (!err.response) {
        errorMsg = '🌐 لا يوجد اتصال بالإنترنت. تحقق من اتصالك وحاول مجدداً';
      } else {
        errorMsg = err.response?.data?.detail || 'حدث خطأ. حاول مرة أخرى';
      }
      setError(errorMsg);
      console.error('Login error:', err);
      // اهتزاز رمز الخطأ ليلفت الانتباه
      setTimeout(() => {
        const errorEl = document.querySelector('[data-testid="login-error"]');
        if (errorEl) errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container islamic-pattern">
      <div className="card" style={{ maxWidth: '500px', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img
            src="https://customer-assets.emergentagent.com/job_halal-match-30/artifacts/3x2jtrfv_IMG_%D9%A2%D9%A0%D9%A2%D9%A6%D9%A0%D9%A1%D9%A0%D9%A2_%D9%A0%D9%A2%D9%A0%D9%A2%D9%A4%D9%A3.png"
            alt="وِداد WeDaD"
            style={{
              width: '220px',
              height: 'auto',
              display: 'block',
              margin: '0 auto 4px'
            }}
          />
          <h2
            style={{
              fontSize: '24px',
              color: 'var(--text-dark)',
              fontFamily: "'Amiri', serif",
              letterSpacing: '1px',
              marginBottom: '6px'
            }}
          >
            تسجيل الدخول
          </h2>
          <p style={{ color: 'var(--text-light)', fontSize: '14px' }}>
            اكمل طريقك نحو شريك الحياة المناسب 💕
          </p>
        </div>

        {error && (
          <div 
            data-testid="login-error" 
            className="message message-error" 
            style={{
              animation: 'errorShake 0.5s ease-in-out',
              border: '2px solid var(--error)',
              fontSize: '14px',
              fontWeight: '600',
              padding: '14px 16px',
              lineHeight: 1.7,
              marginBottom: '16px',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.15)'
            }}
          >
            {error}
          </div>
        )}

        <style>{`
          @keyframes errorShake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-8px); }
            75% { transform: translateX(8px); }
          }
        `}</style>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">البريد الإلكتروني</label>
            <input
              type="email"
              className="input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder="example@email.com"
            />
          </div>

          <div className="form-group password-field">
            <label className="form-label">كلمة المرور</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                className="input password-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                placeholder="كلمة المرور"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
              >
                {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
            </div>
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? '⏳ جاري الدخول...' : '🔐 تسجيل الدخول'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <button 
            onClick={() => navigate('/forgot-password')}
            style={{ background: 'none', border: 'none', color: 'var(--text-light)', cursor: 'pointer', fontSize: '14px' }}
          >
            نسيت كلمة المرور؟
          </button>
        </div>

        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <button 
            onClick={() => navigate('/')}
            style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '15px' }}
          >
            ليس لديك حساب؟ سجل الآن
          </button>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
